import { FC, useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";

import PurposeInputField from "./PurposeInputField";

// @ts-ignore
import rewardsIcon from "assets/images/journeys/purpose/rewards.svg";

// @ts-ignore
import consequencesIcon from "assets/images/journeys/purpose/consequences.svg";

// @ts-ignore
import obstaclesIcon from "assets/images/journeys/purpose/obstacles.svg";

// @ts-ignore
import solutionsIcon from "assets/images/journeys/purpose/solutions.svg";

// @ts-ignore
import vsIcon from "assets/images/journeys/purpose/vs-icon.svg";

// @ts-ignore
import LockedOver from "components/LockedOver/LockedOver";

import { DataType } from "components/InputAreaAI/components/Assistant/data";

import {
  Wrapper,
  Header,
  Title,
  HeaderDivider,
  Section,
  SectionTitle,
  Row,
  Col,
  Divider,
  LockedWrapper
} from "./styled";

interface IPurpose {
  data: any;
  onUpdate: (fields: { [key: string]: any }, areaId: string, goalId: string, callback?: () => void) => void;
  isLocked?: boolean;
}

const Purpose: FC<IPurpose> = ({ data, onUpdate, isLocked }) => {
  const dispatch = useDispatch();

  // Create refs for each input field
  const rewardsRef = useRef<{ focus: () => void; click: () => void; clearValue: () => void } | null>(null);
  const consequencesRef = useRef<{ focus: () => void; click: () => void; clearValue: () => void } | null>(null);
  const obstaclesRef = useRef<{ focus: () => void; click: () => void; clearValue: () => void } | null>(null);
  const solutionsRef = useRef<{ focus: () => void; click: () => void; clearValue: () => void } | null>(null);
  
  // Handle changes to input fields
  const handleChange = useCallback((value: string, field: string, ai?: boolean) => {
    // If the change is from AI, update the value
    if (ai && data?.id && !isLocked) {
      onUpdate({ [field]: value }, data.id, data.goal?.id);
    }
  }, [data?.id, onUpdate, isLocked]);
  
  // Handle saving changes
  const handleSave = useCallback((value: string, field: string) => {
    if (!data?.goal?.id || isLocked)
      return;
    
    // Dispatch action to update the goal
    onUpdate({ [field]: value }, data.id, data.goal?.id);
  }, [data?.goal?.id, dispatch, isLocked]);
  
  // Reset input fields when data.id changes
  useEffect(() => {
    if (data?.id) {
      // Clear all input field values to reset them to their initial values
      if (rewardsRef.current?.clearValue) {
        rewardsRef.current.clearValue();
      }
      if (consequencesRef.current?.clearValue) {
        consequencesRef.current.clearValue();
      }
      if (obstaclesRef.current?.clearValue) {
        obstaclesRef.current.clearValue();
      }
      if (solutionsRef.current?.clearValue) {
        solutionsRef.current.clearValue();
      }
    }
  }, [data?.id]);
  
  return (
    <Wrapper>
      <Header>
        <Title>Purpose</Title>
      </Header>
      
      <HeaderDivider />
      
      <LockedWrapper>
        {isLocked && (
          <LockedOver customContent="This Goal is private" width="50px" />
        )}
        
        <Section>
          <SectionTitle>MOTIVATION</SectionTitle>
          <Row $color={data?.color}>
            <Col>
              <PurposeInputField
                icon={rewardsIcon}
                label="Rewards"
                placeholder="If I do..."
                value={isLocked ? "" : (data?.goal?.pleasure || "")}
                type={DataType.REWARD}
                goalData={{
                  i: data?.i || 0,
                  area: data?.name || "",
                  category: data?.goal?.category_name || "",
                  goal_name: data?.goal?.goal_name || "",
                }}
                onChange={handleChange}
                onSave={handleSave}
                name="pleasure"
                ref={rewardsRef}
                nextInputRef={consequencesRef}
                disabled={isLocked}
              />
            </Col>
            
            <Divider>VS</Divider>
            
            <Col>
              <PurposeInputField
                icon={consequencesIcon}
                label="Consequences"
                placeholder="If I don't..."
                value={isLocked ? "" : (data?.goal?.pain || "")}
                type={DataType.CONSEQUENCE}
                goalData={{
                  i: data?.i || 0,
                  area: data?.name || "",
                  category: data?.goal?.category_name || "",
                  goal_name: data?.goal?.goal_name || "",
                }}
                onChange={handleChange}
                onSave={handleSave}
                name="pain"
                ref={consequencesRef}
                nextInputRef={obstaclesRef}
                disabled={isLocked}
              />
            </Col>
          </Row>
        </Section>
        
        <Section>
          <SectionTitle>STRATEGY</SectionTitle>
          <Row $color={data?.color}>
            <Col>
              <PurposeInputField
                icon={obstaclesIcon}
                label="Obstacles"
                placeholder="What's stopping me?"
                value={isLocked ? "" : (data?.goal?.obstacles || "")}
                type={DataType.OBSTACLES}
                goalData={{
                  i: data?.i || 0,
                  area: data?.name || "",
                  category: data?.goal?.category_name || "",
                  goal_name: data?.goal?.goal_name || "",
                }}
                onChange={handleChange}
                onSave={handleSave}
                name="obstacles"
                ref={obstaclesRef}
                nextInputRef={solutionsRef}
                disabled={isLocked}
              />
            </Col>
            
            <Divider><img src={vsIcon} alt="VS" /></Divider>
            
            <Col>
              <PurposeInputField
                icon={solutionsIcon}
                label="Solutions"
                placeholder="How I'll Do It..."
                value={isLocked ? "" : (data?.goal?.solutions || "")}
                type={DataType.SOLUTIONS}
                goalData={{
                  i: data?.i || 0,
                  area: data?.name || "",
                  category: data?.goal?.category_name || "",
                  goal_name: data?.goal?.goal_name || "",
                }}
                onChange={handleChange}
                onSave={handleSave}
                name="solutions"
                ref={solutionsRef}
                disabled={isLocked}
              />
            </Col>
          </Row>
        </Section>
      </LockedWrapper>
    </Wrapper>
  );
};

export default Purpose;